import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDarkMode } from "../../hooks/useDarkMode";
import { FaArrowLeft } from "react-icons/fa";
import { calculateScore } from "../../utils/seoScoreCalculator";

const PageLevelResultsPage = () => {
  const { projectId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageData, setPageData] = useState(null);

  // Helper function to format endpoint names
  const formatEndpointName = (key) => {
    return key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Helper function to extract and flatten nested endpoints from their parent
  const extractNestedEndpoints = (endpoint) => {
    const endpoints = [];

    // Items that should be flattened from parent endpoints
    // Map the normalized names to actual data keys
    const nestedItems = {
      seo_content_analysis: {
        image_analysis: "Image analysis",
        meta_description: "Meta description",
        word_count: "Word count",
        keyword_density: "Keyword density",
        title_tag: "Title tag",
        on_page_links_summary: "On page links summary",
      },
    };

    const normalizedKey = endpoint.name.toLowerCase().replace(/\s+/g, "_");
    const itemsToExtract = nestedItems[normalizedKey];

    if (itemsToExtract && endpoint.data && typeof endpoint.data === "object") {
      // console.log('[PAGE LEVEL] Extracting nested items from:', normalizedKey);
      // console.log('[PAGE LEVEL] Available data keys:', Object.keys(endpoint.data));

      // Extract each nested item
      Object.entries(itemsToExtract).forEach(([normalizedName, actualKey]) => {
        const itemData = endpoint.data[actualKey];

        if (itemData) {
          // console.log(`[PAGE LEVEL] Found nested item: ${actualKey} (${normalizedName})`, itemData);
          endpoints.push({
            name: formatEndpointName(normalizedName),
            score: calculateScore(normalizedName, itemData),
            status: itemData?.status || "unknown",
            data: itemData,
          });
        } else {
          // console.log(`[PAGE LEVEL] Nested item not found: ${actualKey}`);
        }
      });
    }

    return endpoints;
  };

  useEffect(() => {
    if (state?.page) {
      // Use data from navigation state
      let endpoints = [...(state.page.endpoints || [])];

      // console.log('[PAGE LEVEL] Original endpoints from SeoResultsPage:', endpoints.length, endpoints);

      // Flatten nested endpoints - only process seo_content_analysis
      const flattenedEndpoints = [];

      endpoints.forEach((endpoint) => {
        const normalizedEndpointName = endpoint.name
          .toLowerCase()
          .replace(/\s+/g, "_");

        // SKIP checks removed - deduplication handles this below

        // Add the parent endpoint
        flattenedEndpoints.push(endpoint);

        // Only extract nested items from seo_content_analysis
        if (normalizedEndpointName === "seo_content_analysis") {
          const nestedEndpoints = extractNestedEndpoints(endpoint);
          if (nestedEndpoints.length > 0) {
            // console.log(`[PAGE LEVEL] Adding ${nestedEndpoints.length} nested endpoints from ${endpoint.name}`);
            flattenedEndpoints.push(...nestedEndpoints);
          }
        }
      });

      // Deduplicate by normalized name (prevents duplicate cards)
      const seenNames = new Set();
      const uniqueEndpoints = flattenedEndpoints.filter((endpoint) => {
        const normalizedName = (endpoint.name || "")
          .toLowerCase()
          .replace(/\s+/g, "_");
        if (seenNames.has(normalizedName)) {
          return false;
        }
        seenNames.add(normalizedName);
        return true;
      });

      // Recalculate all scores to ensure they reflect actual data
      const endpointsWithRecalcScore = uniqueEndpoints.map((endpoint) => {
        if (!endpoint.data) {
          return endpoint;
        }
        const normalizedName = (endpoint.name || "")
          .toLowerCase()
          .replace(/\s+/g, "_")
          .replace(/[-\.]/g, "_");
        const recalculatedScore = calculateScore(normalizedName, endpoint.data);
        return {
          ...endpoint,
          score:
            recalculatedScore > 0 ? recalculatedScore : endpoint.score || 0,
        };
      });

      // console.log('[PAGE LEVEL] Final total endpoints:', endpointsWithRecalcScore.length, endpointsWithRecalcScore);

      setPageData({
        page_url: state.page.url,
        page_title: state.page.page_title,
        page_id: state.page.page_id,
        _id: state.page._id, // ✅ Try to use MongoDB _id if available
        analyzed_at: state.page.analyzed_at,
        endpoints: endpointsWithRecalcScore,
      });
      setLoading(false);
    } else {
      setError("No page data available");
      setLoading(false);
    }
  }, [state]);

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return isDarkMode ? "bg-green-900/30" : "bg-green-50";
    if (score >= 60) return isDarkMode ? "bg-yellow-900/30" : "bg-yellow-50";
    return isDarkMode ? "bg-red-900/30" : "bg-red-50";
  };

  const calculatePageScore = () => {
    if (!pageData || !pageData.endpoints) return 0;
    const validScores = pageData.endpoints
      .map((e) => e.score)
      .filter(
        (score) => score !== null && score !== undefined && !isNaN(score),
      );

    if (validScores.length === 0) return 0;
    return Math.round(
      validScores.reduce((a, b) => a + b, 0) / validScores.length,
    );
  };

  // Recalculate endpoint score using proper calculator if needed
  const getEndpointScoreWithRecalc = (endpoint) => {
    if (!endpoint || !endpoint.data) {
      return endpoint?.score || 0;
    }

    // Try to recalculate using proper scorer
    const normalizedName = (endpoint.name || "")
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[-\.]/g, "_");

    const recalculated = calculateScore(normalizedName, endpoint.data);
    return recalculated > 0 ? recalculated : endpoint.score || 0;
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen p-4 sm:p-6 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
      >
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p
              className={`text-sm sm:text-base ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
            >
              Loading page results...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!pageData) {
    return (
      <div
        className={`min-h-screen p-4 sm:p-6 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
      >
        <button
          onClick={() =>
            navigate(
              `/dashboard/projects/${projectId}/seo-results?run_id=${state?.runId}`,
            )
          }
          className={`p-2 rounded-lg transition-colors mb-4 sm:mb-6 ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-white"}`}
        >
          <FaArrowLeft
            className={`text-lg sm:text-xl ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
          />
        </button>
        <div className="text-center">
          <p
            className={`text-sm sm:text-base ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
          >
            Page not found
          </p>
        </div>
      </div>
    );
  }

  const pageScore = calculatePageScore();

  return (
    <div
      className={`min-h-screen p-4 sm:p-6 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
          <button
            onClick={() =>
              navigate(
                `/dashboard/projects/${projectId}/seo-results?run_id=${state?.runId}`,
              )
            }
            className={`p-2 rounded-lg transition-colors flex-shrink-0 ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-white"}`}
          >
            <FaArrowLeft
              className={`text-lg sm:text-xl ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
            />
          </button>
          <div className="min-w-0">
            <h1
              className={`text-xl sm:text-2xl md:text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Page Analysis Results
            </h1>
            <p
              className={`text-xs sm:text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"} break-all truncate`}
            >
              {pageData.page_url}
            </p>
            <p
              className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-500"} mt-1`}
            >
              Run ID: {state?.runId}
            </p>
          </div>
        </div>

        {/* Page Score */}
        <div
          className={`p-4 sm:p-6 rounded-lg shadow-lg mb-6 sm:mb-8 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-0">
            <div className="text-center sm:text-left">
              <h2
                className={`text-xl sm:text-2xl font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Page Score
              </h2>
              <p
                className={`text-xs sm:text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                Based on {pageData.endpoints.length} analysis endpoints
              </p>
            </div>
            <div
              className={`text-center p-6 sm:p-8 rounded-full ${getScoreBgColor(pageScore)}`}
            >
              <div
                className={`text-4xl sm:text-5xl md:text-6xl font-bold ${getScoreColor(pageScore)}`}
              >
                {pageScore}%
              </div>
            </div>
          </div>
        </div>

        {/* Endpoints Grid */}
        <div>
          <h3
            className={`text-lg sm:text-xl md:text-2xl font-bold mb-4 sm:mb-6 ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            Analysis Details
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
            {pageData.endpoints.map((endpoint, index) => (
              <div
                key={`${index}-${endpoint.name}`}
                className={`p-4 sm:p-5 rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer ${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                }`}
                onClick={() =>
                  navigate(
                    `/dashboard/seo-page-results/${projectId}/endpoints/${encodeURIComponent(endpoint.name)}`,
                    {
                      state: {
                        endpoint: endpoint,
                        page: {
                          ...pageData,
                          page_id: pageData.page_id,
                          mongodb_id: pageData.mongodb_id, // Pass MongoDB ObjectId if available
                        },
                        runId: state?.runId,
                        projectId: projectId,
                      },
                    },
                  )
                }
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-xl sm:text-2xl flex-shrink-0">
                      {endpoint.icon}
                    </span>
                    <h4
                      className={`font-semibold text-xs sm:text-sm ${isDarkMode ? "text-white" : "text-gray-900"} line-clamp-2`}
                    >
                      {endpoint.name}
                    </h4>
                  </div>
                  <div
                    className={`px-2 py-1 rounded text-xs font-bold flex-shrink-0 ml-2 ${getScoreBgColor(endpoint.score)} ${getScoreColor(endpoint.score)}`}
                  >
                    {endpoint.score}%
                  </div>
                </div>

                <button
                  onClick={() =>
                    navigate(
                      `/dashboard/seo-page-results/${projectId}/endpoints/${encodeURIComponent(endpoint.name)}`,
                      {
                        state: {
                          endpoint: endpoint,
                          page: pageData,
                          runId: state?.runId,
                          projectId: projectId,
                        },
                      },
                    )
                  }
                  className="w-full mt-3 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors text-xs sm:text-sm"
                >
                  View Details
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageLevelResultsPage;
